import React from 'react';
import {
    Bookmark,
    Calendar,
    Filter,
    X
} from 'lucide-react';

interface HeaderProps {
    view: string;
    activeTopic: string;
    bookmarksCount: number;
    dateFrom: string;
    setDateFrom: (date: string) => void;
    dateTo: string;
    setDateTo: (date: string) => void;
    filterQuery: string;
    setFilterQuery: (query: string) => void;
}

export const Header = ({
    view,
    activeTopic,
    bookmarksCount,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    filterQuery,
    setFilterQuery
}: HeaderProps) => {
    return (
        <header className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 sm:gap-3">
                        {view === 'bookmarks' ? (
                            <>
                                <Bookmark className="text-amber-500 fill-amber-500" />
                                Saved Library
                            </>
                        ) : (
                            <>
                                {activeTopic}
                                <span className="text-sm sm:text-lg font-normal text-slate-400 bg-slate-100 px-2 sm:px-3 py-1 rounded-full">
                                    Latest Research
                                </span>
                            </>
                        )}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-2">
                        {view === 'bookmarks'
                            ? `${bookmarksCount} articles saved for later reading.`
                            : `Curated stream of scientific papers from OpenAlex.`}
                    </p>
                </div>
            </div>

            {/* Result Filtering Bar */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            placeholder="From"
                            className="w-full sm:w-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-10 pr-3 py-3 sm:py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-shadow shadow-sm dark:text-slate-100 dark:[color-scheme:dark]"
                        />
                    </div>
                    <span className="text-slate-400 text-sm">to</span>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            placeholder="To"
                            className="w-full sm:w-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-10 pr-3 py-3 sm:py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-shadow shadow-sm dark:text-slate-100 dark:[color-scheme:dark]"
                        />
                    </div>
                    {(dateFrom || dateTo) && (
                        <button
                            onClick={() => { setDateFrom(''); setDateTo(''); }}
                            className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                            title="Clear date filter"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="relative flex-1">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        value={filterQuery}
                        onChange={(e) => setFilterQuery(e.target.value)}
                        placeholder="Filter results by institution, author, title..."
                        className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-10 pr-4 py-3 sm:py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-shadow shadow-sm dark:text-slate-100 dark:placeholder-slate-500"
                    />
                </div>
            </div>
        </header>
    );
};
